import { api } from "aldoo-ra/API";

// Create API client for spam protection service
const spamAPI = api("spam-protection");

/**
 * Get spam protection system statistics
 * @returns {Promise<Object>} Response with statistics data
 */
export const GetSpamStats = async () => {
    try {
        return await spamAPI({
            get_spam_stats: {}
        });
    } catch (error) {
        console.error("Failed to fetch spam statistics:", error);
        throw error;
    }
};

/**
 * Add an email to the spam list
 * @param {string} email - Email address to add
 * @param {string} reason - Optional reason for blacklisting
 * @param {string} userId - User ID of the person adding the email
 * @param {string} entryType - Entry type: 'blacklist' or 'whitelist' (default: 'blacklist')
 * @returns {Promise<Object>} Response with result
 */
export const AddSpamEmail = async (email, reason, userId, entryType = "blacklist") => {
    try {
        return await spamAPI({
            add_spam_email: {
                email,
                reason,
                userId, // Explicitly pass the user ID
                entryType
            }
        });
    } catch (error) {
        console.error("Failed to add spam email:", error);
        throw error;
    }
};

/**
 * Remove an email from the spam list
 * @param {string} email - Email address to remove
 * @param {string} entryType - Optional entry type to filter by ('blacklist' or 'whitelist')
 * @returns {Promise<Object>} Response with result
 */
export const RemoveSpamEmail = async (email, entryType) => {
    try {
        const params = { email };
        if (entryType) {
            params.entryType = entryType;
        }
        return await spamAPI({
            remove_spam_email: params
        });
    } catch (error) {
        console.error("Failed to remove spam email:", error);
        throw error;
    }
};

/**
 * Get the list of spam emails with pagination
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Response with data, totalItems, totalPages
 */
export const GetSpamList = async (options = {}) => {
    try {
        // Ensure options has all expected properties
        const requestOptions = {
            page: options.page || 1,
            itemsPerPage: options.itemsPerPage || 10,
            search: options.search || null,
            sort: options.sort || { addedAt: -1 }
        };

        // Add entryType filter if specified
        if (options.entryType) {
            requestOptions.entryType = options.entryType;
        }

        // Format exactly as expected by processActionMap
        const response = await spamAPI({
            get_spam_list: requestOptions
        });

        return response;
    } catch (error) {
        console.error("Failed to fetch spam list:", error);
        throw error;
    }
};

/**
 * Get spam rules with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<object>} - API response with rules
 */
export const GetSpamRules = async (options = {}) => {
    try {
        const response = await spamAPI({
            get_spam_rules: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match || {},
                sort: options.sort || { createdAt: -1 },
            },
        });
        return response;
    } catch (error) {
        console.error("Failed to fetch spam rules:", error);
        throw error;
    }
};

/**
 * Add a new spam rule
 * @param {Object} rule - Rule data
 * @returns {Promise<object>} - API response
 */
export const AddSpamRule = async (rule) => {
    try {
        // Ensure description is a string to prevent casting errors
        const ruleData = {
            ...rule,
            description: rule.description !== undefined ?
                (typeof rule.description === 'string' ? rule.description : String(rule.description)) : ''
        };

        const response = await spamAPI({
            add_spam_rule: ruleData
        });
        return response;
    } catch (error) {
        console.error("Failed to add spam rule:", error);
        throw error;
    }
};

/**
 * Update an existing spam rule
 * @param {string} ruleId - Rule ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<object>} - API response
 */
export const UpdateSpamRule = async (ruleId, updates) => {
    try {
        // Ensure description is a string if it's being updated
        const updateData = { ...updates };
        if (updateData.description !== undefined) {
            updateData.description = typeof updateData.description === 'string' ?
                updateData.description : String(updateData.description);
        }

        const response = await spamAPI({
            update_spam_rule: {
                ruleId,
                updates: updateData
            }
        });
        return response;
    } catch (error) {
        console.error("Failed to update spam rule:", error);
        throw error;
    }
};

/**
 * Delete a spam rule
 * @param {string} ruleId - Rule ID
 * @returns {Promise<object>} - API response
 */
export const DeleteSpamRule = async (ruleId) => {
    try {
        const response = await spamAPI({
            delete_spam_rule: {
                ruleId
            }
        });
        return response;
    } catch (error) {
        console.error("Failed to delete spam rule:", error);
        throw error;
    }
};

/**
 * Test an email to see if it would be classified as spam
 * @param {Object} email - Email data to analyze
 * @returns {Promise<Object>} Analysis result
 */
export const AnalyzeEmail = async (email) => {
    try {
        return await spamAPI({
            analyze_email: {
                email
            }
        });
    } catch (error) {
        console.error("Failed to analyze email:", error);
        throw error;
    }
};

// Export all functions as default object
export default {
    GetSpamStats,
    AddSpamEmail,
    RemoveSpamEmail,
    GetSpamList,
    AddSpamRule,
    UpdateSpamRule,
    DeleteSpamRule,
    GetSpamRules,
    AnalyzeEmail
};